// Реестр разделов приложения (по образцу workspaces/registry.ts): одна запись
// описывает роут раздела и его карточки, пункт меню, крошки и справку — роутер
// (router/index.ts), навигация (navigation-menu.ts), useBreadcrumbs и help-registry
// читают отсюда. Новый раздел = запись здесь + ключи в ru.json/en.json.
import {
  Building2, CalendarCheck, ChartColumnBig, ContactRound, FileClock, Handshake,
  Inbox, LayoutDashboard, UserRound, Users,
} from '@lucide/vue'
import type { CapabilityVerb, Role } from '@/services/rbac'
import type { Component } from 'vue'

/**
 * Пункт меню раздела. Видимость пункта — НЕ право доступа: право даёт
 * `SectionDef.capability` (роут-гард), `hiddenFor` лишь убирает пункт у ролей,
 * для которых раздел не рабочий инструмент.
 */
export interface SectionNav {
  /** блок сайдбара: бизнес-разделы (main, по умолчанию) или сервисные (service) */
  group?: 'main' | 'service'
  /** i18n-ключ короткой подписи под лейблом — только в поповере «Ещё» (§6-BIS.1) */
  descriptionKey?: string
  /** false — пункта нет в нижнем таб-баре (место ограничено): сайдбар и «Ещё» */
  tabBar?: boolean
  /** источник счётчика-бейджа; резолвится в AppSidebar/AppTabBar из стора */
  badge?: 'requestsQueue'
  /** роли, которым пункт не показывается, ХОТЯ право на раздел у них есть */
  hiddenFor?: readonly Role[]
}

/** Карточка сущности раздела (deep-link `/:id`) — тот же компонент со split. */
export interface SectionCard {
  /** route name карточки */
  name: string
  path: string
  /** i18n-ключ листовой крошки, пока сущность грузится; нет — крошка ждёт имя */
  entityLabelKey?: string
}

export interface SectionDef {
  /** route name раздела */
  name: string
  path: string
  /** lazy-load страницы — свой чанк; карточка рендерится тем же компонентом */
  component: () => Promise<Component>
  /** i18n-ключ названия раздела (ru.json + en.json) */
  titleKey: string
  icon: Component
  /** право доступа к роуту (роут-гард); нет — раздел открыт любому аутентифицированному */
  capability?: CapabilityVerb
  /** false — раздел без пункта меню (профиль живёт в аватар-меню и последнем табе) */
  nav: SectionNav | false
  card?: SectionCard
  /** общий ключ split-раскладки раздела и карточки — выбор строки не ремаунтит страницу */
  splitKey?: string
}

// Порядок = порядок пунктов в сайдбаре и таб-баре (внутри своей группы).
// `as const satisfies` — чтобы имена роутов остались литералами (SectionName ниже).
const SECTION_DEFS = [
  {
    // Управленческая сводка (KPI + разбивка по отделам поверх отчёта по трафику).
    name: 'dashboard',
    path: '/dashboard',
    component: () => import('@/pages/DashboardPage.vue'),
    titleKey: 'nav.dashboard',
    icon: LayoutDashboard,
    capability: 'view_reports',
    nav: { descriptionKey: 'nav.dashboard_hint', tabBar: false },
  },
  {
    name: 'requests',
    path: '/requests',
    component: () => import('@/pages/RequestsPage.vue'),
    titleKey: 'nav.requests',
    icon: Inbox,
    nav: { badge: 'requestsQueue' },
    splitKey: 'requests',
    card: { name: 'request', path: '/requests/:id(\\d+)', entityLabelKey: 'requests.card_title' },
  },
  {
    // Ресепшн записей не ведёт (RecordPolicy::Scope отдаёт ему пустой список), но право
    // view_records у него есть — записи из таймлайна контрагента он открывает (#542).
    name: 'events',
    path: '/events',
    component: () => import('@/pages/EventFeedPage.vue'),
    titleKey: 'nav.events',
    icon: CalendarCheck,
    capability: 'view_records',
    nav: { hiddenFor: ['reception_admin'] },
    splitKey: 'events',
    card: { name: 'event', path: '/events/:id(\\d+)', entityLabelKey: 'records.card_title' },
  },
  {
    name: 'deals',
    path: '/deals',
    component: () => import('@/pages/DealFeedPage.vue'),
    titleKey: 'nav.deals',
    icon: Handshake,
    capability: 'view_records',
    nav: { hiddenFor: ['reception_admin'] },
    splitKey: 'deals',
    card: { name: 'deal', path: '/deals/:id(\\d+)', entityLabelKey: 'records.deal_card_title' },
  },
  {
    name: 'counterparties',
    path: '/counterparties',
    component: () => import('@/pages/CounterpartiesPage.vue'),
    titleKey: 'nav.counterparties',
    icon: ContactRound,
    nav: {},
    splitKey: 'counterparties',
    card: { name: 'counterparty', path: '/counterparties/:id(\\d+)' },
  },
  {
    // Отчёт по трафику (E14, §4.14): scope выборки enforce'ится BE-политикой.
    name: 'reports',
    path: '/reports',
    component: () => import('@/pages/ReportsPage.vue'),
    titleKey: 'nav.reports',
    icon: ChartColumnBig,
    capability: 'view_reports',
    nav: { descriptionKey: 'nav.reports_hint', tabBar: false },
  },
  {
    // Структура — только founder/commkdir: РКСО (уровень 3) без manage_structure сюда
    // не пускаем, руководитель дивизиона структуру не мутирует (API 403).
    name: 'org-structure',
    path: '/admin/structure',
    component: () => import('@/pages/admin/OrgStructurePage.vue'),
    titleKey: 'nav.structure',
    icon: Building2,
    capability: 'manage_structure',
    nav: { descriptionKey: 'nav.structure_hint', tabBar: false },
  },
  {
    // Пользователи — любой с правом найма (#638); список сужается до зоны на BE.
    name: 'admin-users',
    path: '/admin/users',
    component: () => import('@/pages/admin/UsersListPage.vue'),
    titleKey: 'nav.users',
    icon: Users,
    capability: 'manage_users',
    nav: { group: 'service', descriptionKey: 'nav.users_hint', tabBar: false },
    splitKey: 'users',
    card: { name: 'admin-user', path: '/admin/users/:id(\\d+)' },
  },
  {
    // Журнал аудита (§4.9) — руководители всех уровней (view_audit), не по уровню.
    name: 'audit-log',
    path: '/audit',
    component: () => import('@/pages/AuditLogPage.vue'),
    titleKey: 'nav.audit',
    icon: FileClock,
    capability: 'view_audit',
    nav: { group: 'service', descriptionKey: 'nav.audit_hint', tabBar: false },
  },
  {
    // Профиль — раздел без пункта сайдбара: последний таб у рядовых и аватар-меню.
    name: 'account',
    path: '/account',
    component: () => import('@/pages/AccountPage.vue'),
    titleKey: 'nav.account',
    icon: UserRound,
    nav: false,
  },
] as const satisfies readonly SectionDef[]

export const SECTIONS: readonly SectionDef[] = SECTION_DEFS

/** Имя роута раздела — литеральный union: опечатка в статическом lookup не компилируется. */
export type SectionName = (typeof SECTION_DEFS)[number]['name']

/** Раздел с карточкой — сужение типа для потребителей карточных полей. */
export interface SectionWithCard extends SectionDef {
  card: SectionCard
}

function hasCard(section: SectionDef): section is SectionWithCard {
  return section.card !== undefined
}

export const SECTIONS_WITH_CARD: SectionWithCard[] = SECTIONS.filter(hasCard)

/** Дескриптор по имени роута раздела (профиль, синтетические пункты навигации). */
export const SECTION_BY_NAME = Object.fromEntries(
  SECTIONS.map((section) => [section.name, section]),
) as Record<SectionName, SectionDef>

/** Раздел-владелец карточки по имени её роута — крошки (useBreadcrumbs, lookup по route.name). */
export const SECTION_BY_CARD_ROUTE: Partial<Record<string, SectionWithCard>> = Object.fromEntries(
  SECTIONS_WITH_CARD.map((section) => [section.card.name, section]),
)
